import {
  useGetDomain,
  useGetDomainPages,
  useGetDomainClusters,
  useGetDomainStats,
  useStartCrawl,
  getGetDomainQueryKey,
  getGetDomainPagesQueryKey,
  getGetDomainClustersQueryKey,
  getGetDomainStatsQueryKey,
} from "@workspace/api-client-react";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "wouter";
import { Play, Search, AlertCircle, CheckCircle2, ArrowRight, ArrowUp, ArrowDown, ArrowUpDown, Filter, X, Sparkles, ShieldAlert, FileWarning, Link as LinkIcon, ChevronDown, ChevronUp, Loader2, RotateCcw } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useCrawlCompletionToast, crawlStatusRefetchInterval } from "@/hooks/use-crawl-completion-toast";
import { useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const statusColors: Record<string, string> = {
  pending: "bg-gray-100 text-gray-800",
  crawling: "bg-blue-100 text-blue-800 animate-pulse",
  linking: "bg-blue-100 text-blue-800 animate-pulse",
  clustering: "bg-indigo-100 text-indigo-800 animate-pulse",
  awaiting_approval: "bg-amber-100 text-amber-800",
  analyzing: "bg-purple-100 text-purple-800 animate-pulse",
  done: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
  stopped: "bg-orange-100 text-orange-800",
};

const statusLabels: Record<string, string> = {
  pending: "Bekliyor",
  crawling: "Taranıyor",
  linking: "Linkler İşleniyor",
  clustering: "Cluster Oluşturuluyor",
  awaiting_approval: "Onay Bekliyor",
  analyzing: "Analiz Ediliyor",
  done: "Tamamlandı",
  error: "Hata",
  stopped: "Durduruldu",
};

const LANG_INFO: Record<string, { f: string; n: string }> = {
  en: { f: "🇬🇧", n: "İngilizce" }, tr: { f: "🇹🇷", n: "Türkçe" }, ar: { f: "🇸🇦", n: "Arapça" },
  de: { f: "🇩🇪", n: "Almanca" }, fr: { f: "🇫🇷", n: "Fransızca" }, es: { f: "🇪🇸", n: "İspanyolca" },
  it: { f: "🇮🇹", n: "İtalyanca" }, ru: { f: "🇷🇺", n: "Rusça" }, zh: { f: "🇨🇳", n: "Çince" },
  ja: { f: "🇯🇵", n: "Japonca" }, ko: { f: "🇰🇷", n: "Korece" }, pt: { f: "🇵🇹", n: "Portekizce" },
  nl: { f: "🇳🇱", n: "Felemenkçe" },
};

const scoreColors = (score: number | null | undefined) => {
  if (score === null || score === undefined) return "text-gray-500 bg-gray-100";
  if (score < 55) return "text-red-700 bg-red-100";
  if (score < 85) return "text-amber-700 bg-amber-100";
  return "text-green-700 bg-green-100";
};

type SortKey = "score" | "incoming" | "outgoing" | "warnings" | "url" | null;
type SortDir = "asc" | "desc";

function flattenClusters(
  list: Array<{ id: number; name: string; children?: unknown }>,
  depth = 0,
): Array<{ id: number; name: string; depth: number }> {
  const out: Array<{ id: number; name: string; depth: number }> = [];
  for (const c of list) {
    out.push({ id: c.id, name: c.name, depth });
    const children = (c as { children?: Array<{ id: number; name: string; children?: unknown }> }).children;
    if (Array.isArray(children) && children.length > 0) {
      out.push(...flattenClusters(children, depth + 1));
    }
  }
  return out;
}

export default function DomainDetail() {
  const { id } = useParams<{ id: string }>();
  const domainId = parseInt(id, 10);
  const { data: domain, isLoading: domainLoading } = useGetDomain(domainId, {
    query: {
      enabled: !!domainId,
      queryKey: getGetDomainQueryKey(domainId),
      refetchInterval: crawlStatusRefetchInterval,
    },
  });

  useCrawlCompletionToast({
    domainId,
    status: domain?.status,
    domainUrl: domain?.url,
    totalPages: domain?.totalPages,
    averageScore: domain?.averageScore,
  });

  const [searchTerm, setSearchTerm] = useState("");
  // Cluster filtresini domain bazlı sakla — bir sayfaya girip geri dönünce seçili
  // filtre korunur (önceden her remount'ta "all"a sıfırlanıyordu).
  const clusterFilterKey = `clusterFilter:${domainId}`;
  const [clusterFilter, setClusterFilter] = useState<string>(() => {
    try { return sessionStorage.getItem(`clusterFilter:${domainId}`) ?? "all"; } catch { return "all"; }
  });
  useEffect(() => {
    try {
      if (clusterFilter === "all") sessionStorage.removeItem(clusterFilterKey);
      else sessionStorage.setItem(clusterFilterKey, clusterFilter);
    } catch { /* yoksay */ }
  }, [clusterFilter, clusterFilterKey]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const queryParams = useMemo(() => {
    const p: Record<string, string | number> = {};
    if (searchTerm) p.search = searchTerm;
    if (clusterFilter !== "all") p.clusterId = clusterFilter;
    if (sortBy) {
      p.sortBy = sortBy;
      p.sortDir = sortDir;
    }
    return p;
  }, [searchTerm, clusterFilter, sortBy, sortDir]);

  const { data: pages, isLoading: pagesLoading } = useGetDomainPages(domainId, queryParams, {
    query: { enabled: !!domainId, queryKey: getGetDomainPagesQueryKey(domainId, queryParams) },
  });

  const { data: clusterTree } = useGetDomainClusters(domainId, {
    query: { enabled: !!domainId, queryKey: getGetDomainClustersQueryKey(domainId) },
  });

  // Link inşaası sağlığı özeti (eskiden ayrı "Internal Links" sayfasındaydı — kafa karışıklığını
  // önlemek için buraya taşındı; Pages = Link İnşaası artık tek yüzey).
  const { data: stats } = useGetDomainStats(domainId, { query: { enabled: !!domainId, queryKey: getGetDomainStatsQueryKey(domainId) } });
  const [overviewOpen, setOverviewOpen] = useState(true);

  const flatClusters = useMemo(
    () => (clusterTree ? flattenClusters(clusterTree as Array<{ id: number; name: string; children?: unknown }>) : []),
    [clusterTree],
  );

  const startCrawl = useStartCrawl();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleStartCrawl = async () => {
    try {
      await startCrawl.mutateAsync({ domainId });
      toast({ title: "Tarama başlatıldı" });
      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
    } catch {
      toast({ title: "Tarama başlatılamadı", variant: "destructive" });
    }
  };

  const toggleSort = (key: Exclude<SortKey, null>) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    } else {
      setSortBy(null);
    }
  };

  const sortIcon = (key: Exclude<SortKey, null>) => {
    if (sortBy !== key) return <ArrowUpDown className="w-3 h-3 opacity-40 inline" />;
    return sortDir === "desc" ? <ArrowDown className="w-3 h-3 inline" /> : <ArrowUp className="w-3 h-3 inline" />;
  };

  // "Toplu Link Önerisi Üret" dil seçici state'leri — HOOK KURALI: bu useState'ler
  // early return'lerden ÖNCE olmalı (yoksa loading→loaded geçişinde hook sayısı değişip
  // "Rendered more hooks than during the previous render" hatası fırlatır).
  // Link Fırsatları (arama verisi kesişimi): gösterim alan ama az gövde linki alan sayfalar
  type LinkOppRow = { url: string; pageId: number | null; title: string | null; clicks: number; impressions: number; position: number; inlinks: number | null; ilr: number | null; starved: boolean };
  const { data: linkOpps } = useQuery({
    queryKey: ["link-opportunities", domainId],
    enabled: Number.isFinite(domainId),
    queryFn: async (): Promise<{ connected: boolean; rows: LinkOppRow[]; starvedCount?: number }> => {
      try { const r = await fetch(`/api/domains/${domainId}/link-opportunities`); return r.ok ? await r.json() : { connected: false, rows: [] }; }
      catch { return { connected: false, rows: [] }; }
    },
  });

  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [langs, setLangs] = useState<{ lang: string; pages: number }[] | null>(null);
  const [selectedLangs, setSelectedLangs] = useState<Set<string>>(new Set());
  const [reanalyzing, setReanalyzing] = useState(false);
  const [restarting, setRestarting] = useState(false);

  if (domainLoading) {
    return <div className="p-8">Yükleniyor...</div>;
  }
  if (!domain) {
    return <div className="p-8">Domain bulunamadı.</div>;
  }

  const isCrawling = ["crawling", "linking", "clustering", "analyzing"].includes(domain.status ?? "");
  const isAwaitingApproval = domain.status === "awaiting_approval";
  const progress = domain.crawlProgress || 0;

  // Onay HER ZAMAN ücretsizdir — sadece cluster'ı kabul edip skor+yapısal analize geçirir.
  // Toplu AI link önerisi burada TETİKLENMEZ; aşağıdaki "Toplu Link Önerisi Üret" (dil seçimli,
  // açıkça maliyetli) ayrı, opt-in bir aksiyondur.
  const handleApprove = async () => {
    try {
      const res = await fetch(`/api/domains/${domainId}/approve`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ skipLinks: true }),
      });
      if (!res.ok) throw new Error("Onay gönderilemedi");
      toast({ title: "Onaylandı", description: "Link önerilerini aşağıdan (sayfa-sayfa veya toplu) üretebilirsin." });
      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
    } catch {
      toast({ title: "Onay gönderilemedi", variant: "destructive" });
    }
  };

  // Takılı iş kurtarma: sunucu restart'ı / kredi bitmesi vb. yüzünden yarıda kalan
  // tarama veya analizi kaldığı aşamadan yeniden başlatır. Backend gerçekten çalışan
  // işi ikilemez (409 döner), tarama modunda mevcut sayfalar + cluster'lar korunur.
  const handleRestart = async () => {
    setRestarting(true);
    try {
      const res = await fetch(`/api/domains/${domainId}/restart`, { method: "POST" });
      const data = await res.json().catch(() => ({} as { message?: string; error?: string }));
      if (!res.ok) throw new Error(data.error ?? "Yeniden başlatılamadı");
      toast({ title: data.message ?? "Yeniden başlatıldı" });
      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
    } catch (e) {
      toast({ title: "Yeniden başlatılamadı", description: (e as Error).message || undefined, variant: "destructive" });
    } finally { setRestarting(false); }
  };

  const handleRecluster = async () => {
    try {
      const res = await fetch(`/api/domains/${domainId}/recluster`, { method: "POST" });
      if (!res.ok) throw new Error("Clustering başlatılamadı");
      toast({ title: "Cluster yapısı yeniden oluşturuluyor..." });
      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
    } catch {
      toast({ title: "Clustering başlatılamadı", variant: "destructive" });
    }
  };

  // Yeniden tarama/kümeleme YAPMADAN toplu link analizini (tüm sayfalar, Haiku) çalıştırır.
  // Bu AÇIKÇA opt-in ve maliyetli bir aksiyondur — onay ekranında ARTIK sorulmuyor, sadece
  // burada, dil seçimiyle, kullanıcı bilerek tetiklerse çalışır. (State'ler yukarıda, hook kuralı.)
  const openLangPicker = async () => {
    setLangPickerOpen((o) => !o);
    if (langs) return;
    try {
      const r = await fetch(`/api/domains/${domainId}/languages`);
      if (r.ok) {
        const data = await r.json() as { languages: { lang: string; pages: number }[] };
        setLangs(data.languages);
        setSelectedLangs(new Set(data.languages.map((l) => l.lang)));
      }
    } catch { /* yoksay */ }
  };
  const toggleLang = (l: string) => setSelectedLangs((p) => { const n = new Set(p); n.has(l) ? n.delete(l) : n.add(l); return n; });
  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      const res = await fetch(`/api/domains/${domainId}/analyze`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ languages: [...selectedLangs] }),
      });
      if (!res.ok) throw new Error();
      toast({ title: "Toplu link önerisi üretiliyor…", description: "Yeniden tarama yok — sadece analiz çalışıyor." });
      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
      setLangPickerOpen(false);
    } catch {
      toast({ title: "Analiz başlatılamadı", variant: "destructive" });
    } finally { setReanalyzing(false); }
  };

  const hasClusters = flatClusters.length > 0;

  return (
    <div className="p-4 md:p-6 max-w-(--breakpoint-2xl) mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            {domain.url}
            <Badge variant="outline" className={statusColors[domain.status]}>
              {statusLabels[domain.status]}
            </Badge>
            {domain.crawlSource === "wayback" && (
              <Badge
                variant="outline"
                className="bg-amber-50 text-amber-700 border-amber-200"
                title="Bu site Cloudflare/anti-bot ile korunduğu için doğrudan taranamadı; veriler Internet Arşivi (Wayback Machine) üzerinden, arşivlenmiş son halinden alındı. Güncel olmayabilir."
              >
                Arşivden tarandı
              </Badge>
            )}
          </h1>
          <div className="text-muted-foreground mt-2 flex items-center gap-4 text-sm">
            <span>
              Toplam Sayfa: <strong className="text-foreground">{domain.totalPages || 0}</strong>
            </span>
            <span>•</span>
            <span>
              Taranan: <strong className="text-foreground">{domain.crawledPages || 0}</strong>
            </span>
            <span>•</span>
            <span>
              Ort. Skor: <strong className="text-primary">{domain.averageScore || "-"}</strong>
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          {isAwaitingApproval && (
            <Button onClick={handleApprove} size="lg" className="gap-2 bg-amber-600 hover:bg-amber-700 text-white">
              <CheckCircle2 className="w-4 h-4" />
              Cluster'ları Onayla
            </Button>
          )}
          {domain.status === "done" && (
            <div style={{ position: "relative" }}>
              <Button onClick={openLangPicker} size="lg" variant="outline" className="gap-2">
                <Sparkles className="w-4 h-4" />
                Toplu Link Önerisi Üret
              </Button>
              {langPickerOpen && (
                <div className="bg-white border border-[#e7e7e4] dark:bg-gray-900 dark:border-gray-700" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 320, zIndex: 60, borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.35)", padding: 12 }}>
                  <p className="text-xs text-muted-foreground mb-3">Seçtiğin dillerdeki TÜM sayfalar için AI link önerisi üretir (sayfa başına Haiku — maliyetli). Tek sayfa için Link İnşaası'ndaki "Sayfa Güçlendirme"yi kullanabilirsin.</p>
                  {!langs ? (
                    <div className="text-sm text-muted-foreground">Diller yükleniyor…</div>
                  ) : langs.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Dil tespit edilemedi.</div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {langs.map(({ lang, pages }) => {
                          const info = LANG_INFO[lang] ?? { f: "🌐", n: lang.toUpperCase() };
                          const active = selectedLangs.has(lang);
                          return (
                            <button key={lang} type="button" onClick={() => toggleLang(lang)}
                              className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors"
                              style={{ borderColor: active ? "#12b76a" : "#e7e7e4", background: active ? "rgba(34,197,94,0.14)" : "transparent", color: active ? "#12b76a" : "#1d1d1b" }}>
                              <span>{info.f}</span><span className="font-medium">{info.n}</span><span className="text-muted-foreground">{pages}</span>
                            </button>
                          );
                        })}
                      </div>
                      <Button onClick={handleReanalyze} disabled={reanalyzing || selectedLangs.size === 0} size="sm" className="w-full gap-2">
                        {reanalyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {selectedLangs.size} dil için üret
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {(isCrawling || domain.status === "error" || domain.status === "stopped") && (
            <Button
              onClick={handleRestart}
              disabled={restarting}
              size="lg"
              variant="outline"
              className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-50"
              title="Tarama/analiz takılı kaldıysa (sunucu yeniden başladı, kredi bitti vb.) kaldığı aşamadan yeniden başlatır. Mevcut sayfalar ve cluster yapısı korunur; gerçekten çalışan bir işi ikilemez."
            >
              {restarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Taramayı Yeniden Başlat
            </Button>
          )}
          <Button onClick={handleStartCrawl} disabled={isCrawling || startCrawl.isPending} size="lg" className="gap-2">
            <Play className="w-4 h-4" />
            Tekrar Tara
          </Button>
        </div>
      </div>

      {isAwaitingApproval && (
        <Card className={hasClusters ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}>
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 mt-0.5 shrink-0 ${hasClusters ? "text-amber-600" : "text-red-600"}`} />
              <div className="flex-1">
                {hasClusters ? (
                  <>
                    <p className="font-medium text-amber-800">Cluster yapısı oluşturuldu — onayınızı bekliyor</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Aşağıdaki cluster'ları inceleyin. Hazır olduğunuzda "Onayla &amp; Analiz Başlat" butonuna tıklayın.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-red-800">Cluster oluşturulamadı — AI bir hatayla karşılaştı</p>
                    <p className="text-sm text-red-700 mt-1 mb-3">
                      Cluster yapısı otomatik oluşturulamadı. "Yeniden Cluster Oluştur" butonuna tıklayarak tekrar deneyin.
                    </p>
                    <Button onClick={handleRecluster} size="sm" variant="outline" className="border-red-400 text-red-700 hover:bg-red-100">
                      Yeniden Cluster Oluştur
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isCrawling && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-primary">Tarama Devam Ediyor...</span>
              <span className="text-sm font-medium">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Link inşaası sağlığı özeti — eskiden ayrı "Internal Links" sayfasındaydı, tek yüzeye taşındı */}
      {stats && (
        <div>
          <button onClick={() => setOverviewOpen((o) => !o)} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-3">
            {overviewOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Link İnşaası Sağlığı — Genel Bakış
          </button>
          {overviewOpen && (
            <div className="space-y-6 mb-2">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                      <ShieldAlert className="w-5 h-5" />
                      Kritik Uyarılar
                    </div>
                    <div className="text-3xl font-bold text-red-900">{stats.criticalPages || 0}</div>
                    <div className="text-sm text-red-700 mt-1">sayfada sorun var</div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2">
                      <AlertCircle className="w-5 h-5" />
                      Orta Uyarılar
                    </div>
                    <div className="text-3xl font-bold text-amber-900">{stats.mediumPages || 0}</div>
                    <div className="text-sm text-amber-700 mt-1">sayfada geliştirme gerekli</div>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                      <FileWarning className="w-5 h-5" />
                      İyi Durum
                    </div>
                    <div className="text-3xl font-bold text-green-900">{stats.goodPages || 0}</div>
                    <div className="text-sm text-green-700 mt-1">sayfa sorunsuz</div>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-blue-800 font-semibold mb-2">
                      <LinkIcon className="w-5 h-5" />
                      Kırık Linkler
                    </div>
                    <div className="text-3xl font-bold text-blue-900">{stats.brokenLinks || 0}</div>
                    <div className="text-sm text-blue-700 mt-1">adet tespit edildi</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>En Sık Görülen Uyarı Tipleri</CardTitle>
                    <CardDescription>Sitenizdeki yapısal hataların dağılımı</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats.topWarningTypes && stats.topWarningTypes.length > 0 ? (
                      <div className="space-y-4">
                        {stats.topWarningTypes.map((stat, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm font-medium">{stat.label}</span>
                            <Badge variant="secondary">{stat.count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">Uyarı bulunmuyor.</div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Skor Dağılımı</CardTitle>
                    <CardDescription>Tüm sayfaların 100 üzerinden SEO linkleme skoru</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats.scoreDistribution && stats.scoreDistribution.length > 0 ? (
                      <div className="space-y-4">
                        {stats.scoreDistribution.map((bucket, i) => (
                          <div key={i} className="flex items-center gap-4">
                            <div className="w-24 text-sm text-muted-foreground">{bucket.label}</div>
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${(bucket.count / stats.totalPages) * 100}%`, backgroundColor: bucket.color }} />
                            </div>
                            <div className="w-12 text-right text-sm font-medium">{bucket.count}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">Yeterli veri yok.</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      )}

      {/* LİNK FIRSATLARI — arama verisi × link grafiği kesişimi: talep kanıtlı ama link aç sayfalar */}
      {linkOpps?.connected && (linkOpps.rows?.filter((r) => r.starved).length ?? 0) > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🔗 Link Fırsatları
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{linkOpps.rows.filter((r) => r.starved).length} link aç sayfa</span>
            </CardTitle>
            <CardDescription>
              Aramada gösterim alan (talep kanıtlı) ama içerik gövdesinden 3'ten az link alan sayfalar — link inşasında EN YÜKSEK öncelik.
              Sayfa detayında "Detaylı AI ile Tarat" ile plan üretin.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left font-medium px-4 py-2">Sayfa</th>
                  <th className="text-right font-medium px-3 py-2">Gösterim (28g)</th>
                  <th className="text-right font-medium px-3 py-2">Tıklama</th>
                  <th className="text-right font-medium px-3 py-2">Poz.</th>
                  <th className="text-right font-medium px-3 py-2">Gövde Linki</th>
                  <th className="text-right font-medium px-4 py-2">ILR</th>
                </tr>
              </thead>
              <tbody>
                {linkOpps.rows.filter((r) => r.starved).slice(0, 10).map((r) => (
                  <tr key={r.url} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-2 max-w-[420px]">
                      {r.pageId ? (
                        <Link href={`/pages/${r.pageId}`} className="text-primary hover:underline truncate block" title={r.url}>
                          {r.title || r.url}
                        </Link>
                      ) : (
                        <span className="truncate block" title={r.url}>{r.title || r.url}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{r.impressions.toLocaleString("tr-TR")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.clicks.toLocaleString("tr-TR")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.position}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-700">{r.inlinks ?? "-"}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-indigo-600">{r.ilr ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <div className="p-4 border-b flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div style={{ position: "relative", flex: 1, maxWidth: 420 }}>
            <Search size={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#70706c" }} />
            <input
              placeholder="URL veya başlıkta ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="pages-search"
              className="border border-[#e7e7e4] bg-[#f4f4f2] text-[#1d1d1b] placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" style={{ width: "100%", borderRadius: 8, padding: "8px 30px 8px 34px", fontSize: 13, outline: "none" }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} title="Temizle"
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#70706c", cursor: "pointer", display: "flex", padding: 2 }}>
                <X size={14} />
              </button>
            )}
          </div>

          {/* Cluster filtresi - clusters sayfasindaki ozel dropdown stili */}
          <div style={{ position: "relative" }}>
            {(() => {
              const activeLabel = clusterFilter === "all" ? "Tümü"
                : clusterFilter === "null" ? "Cluster'siz"
                : (flatClusters.find((c) => String(c.id) === clusterFilter)?.name.replace(/^\[[A-Z]{2}\] /, "") ?? "Cluster");
              const isActive = clusterFilter !== "all";
              return (
                <button onClick={() => setFilterOpen((o) => !o)} data-testid="cluster-filter"
                  className={isActive ? "" : "bg-[#f4f4f2] text-[#70706c] dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"}
                  style={{ display: "flex", alignItems: "center", gap: 8, border: `1px solid ${isActive ? "#e83e3c" : "#e7e7e4"}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 500, ...(isActive ? { color: "#e83e3c", background: "rgba(139,92,246,0.12)" } : {}), cursor: "pointer", minWidth: 210, justifyContent: "space-between" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}><Filter size={14} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeLabel}</span></span>
                </button>
              );
            })()}
            {filterOpen && (
              <div className="bg-white border border-[#e7e7e4] dark:bg-gray-900 dark:border-gray-700" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 300, zIndex: 60, borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.35)", padding: 8 }}>
                <input autoFocus value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} placeholder="Cluster filtrele..."
                  className="border border-[#e7e7e4] bg-[#f4f4f2] text-[#1d1d1b] placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" style={{ width: "100%", borderRadius: 7, padding: "6px 9px", fontSize: 12, outline: "none", marginBottom: 8 }} />
                <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                  {[{ id: "all", label: "Tümü", lang: null as string | null, depth: 0 }, { id: "null", label: "Cluster'siz", lang: null as string | null, depth: 0 }]
                    .concat(flatClusters.map((c) => ({ id: String(c.id), label: c.name.replace(/^\[[A-Z]{2}\] /, ""), lang: c.name.match(/^\[([A-Z]{2})\] /)?.[1] ?? null, depth: c.depth })))
                    .filter((c) => !filterQuery.trim() || c.label.toLowerCase().includes(filterQuery.trim().toLowerCase()))
                    .map((c) => (
                      <button key={c.id} onClick={() => { setClusterFilter(c.id); setFilterOpen(false); setFilterQuery(""); }}
                        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", border: "none", cursor: "pointer", borderRadius: 7, padding: "7px 9px", paddingLeft: 9 + c.depth * 14, fontSize: 12, background: clusterFilter === c.id ? "rgba(139,92,246,0.18)" : "transparent", color: clusterFilter === c.id ? "#e83e3c" : "#1d1d1b" }}
                        onMouseEnter={(e) => { if (clusterFilter !== c.id) e.currentTarget.style.background = "#f4f4f2"; }}
                        onMouseLeave={(e) => { if (clusterFilter !== c.id) e.currentTarget.style.background = "transparent"; }}>
                        {c.depth > 0 && <span className="text-gray-500 dark:text-gray-400">↳</span>}
                        {c.lang && <span style={{ fontSize: 9, fontWeight: 700, color: "#70706c", border: "1px solid #e7e7e4", borderRadius: 4, padding: "0 4px" }}>{c.lang}</span>}
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {pagesLoading ? (
          <div className="p-8 text-center text-muted-foreground">Sayfalar yükleniyor...</div>
        ) : pages && pages.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">
                  <button
                    type="button"
                    onClick={() => toggleSort("score")}
                    className="flex items-center gap-1 hover:text-foreground"
                    data-testid="sort-score"
                  >
                    Skor {sortIcon("score")}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => toggleSort("url")}
                    className="flex items-center gap-1 hover:text-foreground"
                    data-testid="sort-url"
                  >
                    URL {sortIcon("url")}
                  </button>
                </TableHead>
                <TableHead>Cluster</TableHead>
                <TableHead className="text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort("incoming")}
                    className="ml-auto flex items-center gap-1 hover:text-foreground"
                    data-testid="sort-incoming"
                  >
                    İç Link {sortIcon("incoming")}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort("outgoing")}
                    className="ml-auto flex items-center gap-1 hover:text-foreground"
                    data-testid="sort-outgoing"
                  >
                    Dış Link {sortIcon("outgoing")}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort("warnings")}
                    className="ml-auto flex items-center gap-1 hover:text-foreground"
                    data-testid="sort-warnings"
                  >
                    Uyarılar {sortIcon("warnings")}
                  </button>
                </TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.id} className="hover:bg-muted/50 transition-colors">
                  <TableCell>
                    <div
                      className={`w-10 h-10 rounded flex items-center justify-center font-bold text-sm ${scoreColors(page.score)}`}
                    >
                      {page.score || "-"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-sm truncate max-w-[400px]" title={page.title || page.url}>
                      {page.title || "İsimsiz Sayfa"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-[400px]">{page.url}</div>
                    {page.primaryKeyword && (
                      <div className="text-xs text-primary/80 mt-0.5 truncate max-w-[400px]">
                        🏷️ {page.primaryKeyword}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {page.clusterName ? (
                      <Badge variant="secondary" className="font-normal">
                        {page.clusterName}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">{page.incomingLinkCount}</TableCell>
                  <TableCell className="text-right font-medium">{page.outgoingLinkCount}</TableCell>
                  <TableCell className="text-right">
                    {page.warningCount > 0 ? (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {page.warningCount}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Kusursuz
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Link href={`/pages/${page.id}`}>
                      <Button variant="ghost" size="icon">
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-16 text-center">
            <h3 className="text-lg font-medium">Sayfa bulunamadı</h3>
            <p className="text-muted-foreground mt-1">
              Arama kriterlerine uyan sayfa yok veya henüz tarama tamamlanmadı.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}